import {
  OTA_COMMAND_CHAR_UUID,
  OTA_FIRMWARE_CHAR_UUID,
  OTA_SERVICE_UUID,
} from '../ota/protocol';
import {
  BATTERY_SERVICE_UUID,
  DEVICE_INFO_SERVICE_UUID,
  FIRMWARE_REVISION_CHAR_UUID,
  HARDWARE_REVISION_CHAR_UUID,
  MANUFACTURER_NAME_CHAR_UUID,
} from './uuids';
import {
  COMMAND_UNLOCK_PASSPHRASE,
  parseGetStatusResponse,
  parsePowerStatus,
  parseSettingsStatus,
  parseTemperatureStatus,
  type PowerStatus,
  type SettingsStatus,
  type TemperatureStatus,
  TOOLBOX_COMMAND_CHAR_UUID,
  TOOLBOX_POWER_STATUS_CHAR_UUID,
  TOOLBOX_SERVICE_UUID,
  TOOLBOX_STATUS_SERVICE_UUID,
  TOOLBOX_SETTINGS_STATUS_CHAR_UUID,
  TOOLBOX_TEMPERATURE_STATUS_CHAR_UUID,
} from './toolbox-protocol';

export type DeviceInfo = {
  name: string;
  id: string;
  manufacturer?: string;
  firmwareRevision?: string;
  hardwareRevision?: string;
  serialNumber?: string;
  macAddress?: string;
  model?: string;
  centerFrequency?: number;
  currentFrequency?: number;
  autoTuneMode?: string;
  sweepActive?: boolean;
  voltageBattery?: number;
  current?: number;
  powerWatts?: number;
  temperaturePCB?: number;
  temperatureESP32?: number;
  temperatureBattery?: number;
  odometerTotalMinutes?: number;
};

export type TransportCapabilities = {
  ota: boolean;
  command: boolean;
  powerStatus: boolean;
  temperatureStatus: boolean;
  settingsStatus: boolean;
};

export type SetActiveTuningTargetResult = {
  command: 'SetActiveTuningTarget' | 'SetCenterFrequency';
  mode: 'target-aware' | 'legacy-center-frequency';
  targetId: string | null;
  centerHz: number;
  fallbackReason: string | null;
  response: string | null;
};

export type BleConnectOptions = {
  namePrefix?: string;
  reuseDevice?: boolean;
  timeoutMs?: number;
  maxRetries?: number;
};

export type BleDiagnosticEvent = {
  level: 'debug' | 'info' | 'warning' | 'error';
  event: string;
  details?: Record<string, unknown>;
};

export type BleNotificationKind = 'command' | 'power' | 'temperature' | 'settings';

export type BleDiagnosticSnapshot = {
  capturedAtIso: string;
  connected: boolean;
  notifications: Record<
    BleNotificationKind,
    {
      received: number;
      parsed: number;
      parseFailures: number;
      lastByteLength: number;
      lastReceivedAtIso: string | null;
      lastReceivedMsAgo: number | null;
    }
  >;
};

const COMMAND_BUFFER_IDLE_FLUSH_MS = 35;
const ACTIVE_TUNING_TARGET_RESPONSE_TIMEOUT_MS = 1200;
const SWEEP_TEXT_TOKEN_REGEX = /^f\d+:p[+-]?(?:\d+\.?\d*|\.\d+)/;
type ActiveTuningTargetSupport = 'unknown' | 'supported' | 'unsupported';
type NotificationMetric = {
  received: number;
  parsed: number;
  parseFailures: number;
  lastByteLength: number;
  lastReceivedAtIso: string | null;
};

export class BleTransport {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;

  private otaCommandCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private otaFirmwareCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private toolboxCommandCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private powerStatusCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private temperatureStatusCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private settingsStatusCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;

  private otaCommandListener: EventListener | null = null;
  private otaFirmwareListener: EventListener | null = null;
  private toolboxCommandListener: EventListener | null = null;
  private powerStatusListener: EventListener | null = null;
  private temperatureStatusListener: EventListener | null = null;
  private settingsStatusListener: EventListener | null = null;

  private disconnectHandlers = new Set<() => void>();
  private deviceInfoHandlers = new Set<(info: DeviceInfo | null) => void>();
  private textLineHandlers = new Set<(line: string) => void>();
  private settingsHandlers = new Set<(settings: SettingsStatus) => void>();
  private diagnosticHandlers = new Set<(event: BleDiagnosticEvent) => void>();

  private capabilities: TransportCapabilities = {
    ota: false,
    command: false,
    powerStatus: false,
    temperatureStatus: false,
    settingsStatus: false,
  };

  private deviceInfo: DeviceInfo | null = null;
  private latestPowerStatus: PowerStatus | null = null;
  private latestTemperatureStatus: TemperatureStatus | null = null;
  private latestSettingsStatus: SettingsStatus | null = null;

  private keepDeviceOnDisconnect = false;
  private commandLineBuffer = '';
  private commandBufferFlushTimer: number | null = null;
  private commandUnlocked = false;
  private textCommandWriteChain: Promise<void> = Promise.resolve();
  private activeTuningTargetSupport: ActiveTuningTargetSupport = 'unknown';
  private notificationMetrics = createNotificationMetrics();

  async connect(options: BleConnectOptions = {}): Promise<DeviceInfo> {
    if (!navigator.bluetooth) {
      throw new Error('Web Bluetooth is not supported in this browser.');
    }

    const reuseDevice = options.reuseDevice ?? false;
    const maxRetries = options.maxRetries ?? (reuseDevice ? 1 : 3);
    const timeoutMs = options.timeoutMs ?? (reuseDevice ? 3000 : 12000);
    const setupTimeoutMs = Math.max(timeoutMs, 4000);
    const retryDelayMs = 1000;
    this.emitDiagnostic('info', 'connect_requested', {
      reuseDevice,
      maxRetries,
      timeoutMs,
      setupTimeoutMs,
      namePrefix: options.namePrefix ?? null,
    });

    let selectedDevice = this.device;

    if (!reuseDevice) {
      const requestOptions: RequestDeviceOptions = {
        optionalServices: [
          OTA_SERVICE_UUID,
          DEVICE_INFO_SERVICE_UUID,
          TOOLBOX_SERVICE_UUID,
          TOOLBOX_STATUS_SERVICE_UUID,
          BATTERY_SERVICE_UUID,
        ],
      };

      if (options.namePrefix) {
        requestOptions.filters = [{ namePrefix: options.namePrefix }];
      } else {
        requestOptions.acceptAllDevices = true;
      }

      selectedDevice = await navigator.bluetooth.requestDevice(requestOptions);
      this.emitDiagnostic('info', 'device_selected', {
        deviceName: selectedDevice.name ?? null,
        deviceId: selectedDevice.id,
      });
      if (!selectedDevice.gatt) {
        throw new Error('No GATT server available on the selected device.');
      }
    } else if (!selectedDevice?.gatt) {
      throw new Error('No previously paired device available for reconnect.');
    }

    this.device = selectedDevice;
    this.device.removeEventListener('gattserverdisconnected', this.handleDisconnect);
    this.device.addEventListener('gattserverdisconnected', this.handleDisconnect);

    let lastError: unknown = null;

    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      const attemptStartedAtMs = Date.now();
      this.emitDiagnostic('info', 'connect_attempt_started', {
        attempt,
        maxRetries,
      });
      try {
        this.resetSessionState();

        this.server = await this.withTimeout(
          selectedDevice.gatt.connect(),
          timeoutMs,
          'Connection timed out.'
        );

        if (!this.server.connected) {
          throw new Error('Failed to connect to the device.');
        }

        this.deviceInfo = {
          name: selectedDevice.name ?? 'Unknown device',
          id: selectedDevice.id,
        };

        await this.withTimeout(
          (async () => {
            await this.setupOtaCharacteristics();
            await this.setupToolboxCharacteristics();
            await this.loadDeviceInfo();
          })(),
          setupTimeoutMs,
          'Connection setup timed out.'
        );

        const connectedInfo = this.deviceInfo;
        if (!connectedInfo || !this.server?.connected || !selectedDevice.gatt?.connected) {
          throw new Error('Device disconnected during connection setup.');
        }

        this.emitDeviceInfoChanged();
        this.emitDiagnostic('info', 'connect_attempt_succeeded', {
          attempt,
          durationMs: Date.now() - attemptStartedAtMs,
          deviceName: connectedInfo.name,
          deviceId: connectedInfo.id,
          firmwareRevision: connectedInfo.firmwareRevision ?? null,
          hardwareRevision: connectedInfo.hardwareRevision ?? null,
          capabilities: this.getCapabilities(),
        });
        return connectedInfo;
      } catch (error) {
        lastError = error;
        this.emitDiagnostic('error', 'connect_attempt_failed', {
          attempt,
          maxRetries,
          durationMs: Date.now() - attemptStartedAtMs,
          error: describeDiagnosticError(error),
        });
        try {
          if (this.server?.connected) {
            this.server.disconnect();
          }
        } catch {
          // Ignore disconnect cleanup failures.
        }
        this.resetSessionState();

        if (attempt < maxRetries) {
          await this.delay(retryDelayMs);
        }
      }
    }

    if (lastError instanceof Error) {
      this.emitDiagnostic('error', 'connect_failed', {
        error: describeDiagnosticError(lastError),
      });
      throw lastError;
    }
    this.emitDiagnostic('error', 'connect_failed', {
      error: describeDiagnosticError(lastError),
    });
    throw new Error('Failed to connect to the device.');
  }

  disconnect(): void {
    this.emitDiagnostic('info', 'disconnect_requested', {
      connected: this.isConnected(),
    });
    this.keepDeviceOnDisconnect = false;
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.reset(false);
  }

  requestDisconnectForReconnect(): boolean {
    if (!this.device?.gatt?.connected) {
      return false;
    }
    try {
      this.device.gatt.disconnect();
      return true;
    } catch {
      return false;
    }
  }

  isConnected(): boolean {
    return Boolean(this.device?.gatt?.connected);
  }

  canReconnect(): boolean {
    return Boolean(this.device?.gatt);
  }

  setKeepDeviceOnDisconnect(value: boolean): void {
    this.keepDeviceOnDisconnect = value;
  }

  onDisconnect(handler: () => void): () => void {
    this.disconnectHandlers.add(handler);
    return () => this.disconnectHandlers.delete(handler);
  }

  onDeviceInfoChanged(handler: (info: DeviceInfo | null) => void): () => void {
    this.deviceInfoHandlers.add(handler);
    return () => this.deviceInfoHandlers.delete(handler);
  }

  onTextLine(handler: (line: string) => void): () => void {
    this.textLineHandlers.add(handler);
    return () => this.textLineHandlers.delete(handler);
  }

  onSettingsStatus(handler: (settings: SettingsStatus) => void): () => void {
    this.settingsHandlers.add(handler);
    return () => this.settingsHandlers.delete(handler);
  }

  onDiagnosticEvent(handler: (event: BleDiagnosticEvent) => void): () => void {
    this.diagnosticHandlers.add(handler);
    return () => this.diagnosticHandlers.delete(handler);
  }

  getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }

  getCapabilities(): TransportCapabilities {
    return { ...this.capabilities };
  }

  getLatestSettingsStatus(): SettingsStatus | null {
    return this.latestSettingsStatus;
  }

  getLatestPowerStatus(): PowerStatus | null {
    return this.latestPowerStatus;
  }

  getLatestTemperatureStatus(): TemperatureStatus | null {
    return this.latestTemperatureStatus;
  }

  getDiagnosticSnapshot(): BleDiagnosticSnapshot {
    const capturedAtMs = Date.now();
    const notifications = Object.fromEntries(
      (Object.entries(this.notificationMetrics) as Array<[BleNotificationKind, NotificationMetric]>).map(
        ([kind, metric]) => {
          const lastReceivedAtMs =
            metric.lastReceivedAtIso === null ? Number.NaN : Date.parse(metric.lastReceivedAtIso);
          return [
            kind,
            {
              ...metric,
              lastReceivedMsAgo: Number.isFinite(lastReceivedAtMs)
                ? Math.max(0, capturedAtMs - lastReceivedAtMs)
                : null,
            },
          ];
        }
      )
    ) as BleDiagnosticSnapshot['notifications'];

    return {
      capturedAtIso: new Date(capturedAtMs).toISOString(),
      connected: this.isConnected(),
      notifications,
    };
  }

  async writeCommand(data: Uint8Array): Promise<void> {
    if (!this.otaCommandCharacteristic) {
      throw new Error('OTA command characteristic not available.');
    }
    await this.otaCommandCharacteristic.writeValue(data);
  }

  async writeFirmware(data: Uint8Array): Promise<void> {
    if (!this.otaFirmwareCharacteristic) {
      throw new Error('OTA firmware characteristic not available.');
    }
    const characteristic = this.otaFirmwareCharacteristic as BluetoothRemoteGATTCharacteristic & {
      writeValueWithoutResponse?: (value: BufferSource) => Promise<void>;
    };
    if (typeof characteristic.writeValueWithoutResponse === 'function') {
      await characteristic.writeValueWithoutResponse(data);
    } else {
      await characteristic.writeValue(data);
    }
  }

  async subscribeCommandNotifications(handler: (view: DataView) => void): Promise<void> {
    if (!this.otaCommandCharacteristic) {
      throw new Error('OTA command characteristic not available.');
    }
    if (this.otaCommandListener) {
      this.otaCommandCharacteristic.removeEventListener('characteristicvaluechanged', this.otaCommandListener);
    }
    this.otaCommandListener = (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic;
      if (target?.value) {
        handler(target.value);
      }
    };
    await this.otaCommandCharacteristic.startNotifications();
    this.otaCommandCharacteristic.addEventListener('characteristicvaluechanged', this.otaCommandListener);
  }

  async subscribeFirmwareNotifications(handler: (view: DataView) => void): Promise<void> {
    if (!this.otaFirmwareCharacteristic) {
      throw new Error('OTA firmware characteristic not available.');
    }
    if (this.otaFirmwareListener) {
      this.otaFirmwareCharacteristic.removeEventListener('characteristicvaluechanged', this.otaFirmwareListener);
    }
    this.otaFirmwareListener = (event: Event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic;
      if (target?.value) {
        handler(target.value);
      }
    };
    await this.otaFirmwareCharacteristic.startNotifications();
    this.otaFirmwareCharacteristic.addEventListener('characteristicvaluechanged', this.otaFirmwareListener);
  }

  async unlockCommandChannel(passphrase = COMMAND_UNLOCK_PASSPHRASE): Promise<void> {
    if (!this.toolboxCommandCharacteristic) {
      throw new Error('Command channel is not available on this device.');
    }
    if (this.commandUnlocked) {
      return;
    }
    await this.sendTextCommand(passphrase);
    this.commandUnlocked = true;
    await this.delay(120);
  }

  async sendTextCommand(command: string): Promise<void> {
    if (!this.toolboxCommandCharacteristic) {
      throw new Error('Command channel is not available on this device.');
    }

    const payload = new TextEncoder().encode(command);
    const commandSummary = describeTextCommand(command);
    const writeStartedAtMs = Date.now();
    this.emitDiagnostic('debug', 'command_write_started', {
      command: commandSummary,
      byteLength: payload.byteLength,
    });
    const task = this.textCommandWriteChain.then(async () => {
      if (!this.toolboxCommandCharacteristic) {
        throw new Error('Command channel became unavailable.');
      }
      try {
        await this.toolboxCommandCharacteristic.writeValue(payload);
        this.emitDiagnostic('debug', 'command_write_completed', {
          command: commandSummary,
          byteLength: payload.byteLength,
          durationMs: Date.now() - writeStartedAtMs,
        });
      } catch (error) {
        this.emitDiagnostic('error', 'command_write_failed', {
          command: commandSummary,
          byteLength: payload.byteLength,
          durationMs: Date.now() - writeStartedAtMs,
          error: describeDiagnosticError(error),
        });
        throw error;
      }
    });

    this.textCommandWriteChain = task.catch(() => undefined);
    await task;
  }

  async sendTextCommandWithResponse(
    command: string,
    timeoutMs = 5000,
    idleQuietMs = 180
  ): Promise<string> {
    if (!this.toolboxCommandCharacteristic) {
      throw new Error('Command channel is not available on this device.');
    }

    return new Promise((resolve, reject) => {
      const lines: string[] = [];
      let settled = false;
      let timeoutId: number | null = null;
      let idleId: number | null = null;

      const unsubscribe = this.onTextLine((line) => {
        lines.push(line);
        if (idleId) {
          window.clearTimeout(idleId);
        }
        idleId = window.setTimeout(() => {
          finalize();
        }, idleQuietMs);
      });

      const cleanup = (): void => {
        unsubscribe();
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        if (idleId) {
          window.clearTimeout(idleId);
        }
      };

      const finalize = (error?: Error): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        if (error) {
          reject(error);
          return;
        }
        if (lines.length === 0) {
          reject(new Error(`No response received for command: ${command}`));
          return;
        }
        resolve(lines.join('\n'));
      };

      timeoutId = window.setTimeout(() => {
        if (lines.length > 0) {
          finalize();
        } else {
          finalize(new Error(`Command timed out: ${command}`));
        }
      }, timeoutMs);

      this.sendTextCommand(command).catch((error) => {
        if (error instanceof Error) {
          finalize(error);
        } else {
          finalize(new Error(`Failed to send command: ${command}`));
        }
      });
    });
  }

  async setActiveTuningTargetWithFallback(
    targetId: string,
    centerHz: number,
    options: {
      responseTimeoutMs?: number;
      responseIdleQuietMs?: number;
      verifyTimeoutMs?: number;
    } = {}
  ): Promise<SetActiveTuningTargetResult> {
    const normalizedTargetId = targetId.trim();
    if (!normalizedTargetId) {
      throw new Error('Tuning target id is required.');
    }

    const normalizedCenterHz = Math.round(centerHz);
    const responseTimeoutMs =
      options.responseTimeoutMs ??
      options.verifyTimeoutMs ??
      ACTIVE_TUNING_TARGET_RESPONSE_TIMEOUT_MS;
    const responseIdleQuietMs = options.responseIdleQuietMs ?? 180;

    if (this.activeTuningTargetSupport === 'unsupported') {
      return this.setCenterFrequencyFallback(
        normalizedCenterHz,
        'SetActiveTuningTarget is unsupported on this firmware.'
      );
    }

    try {
      const response = await this.sendTextCommandWithResponse(
        `SetActiveTuningTarget ${normalizedTargetId} ${normalizedCenterHz}`,
        responseTimeoutMs,
        responseIdleQuietMs
      );
      const parsed = parseActiveTuningTargetResponse(response);

      if (parsed.kind === 'ok') {
        if (parsed.targetId !== normalizedTargetId) {
          throw new ActiveTuningTargetResponseError(
            `SetActiveTuningTarget acknowledged ${parsed.targetId} instead of ${normalizedTargetId}.`
          );
        }
        if (parsed.centerHz !== normalizedCenterHz) {
          throw new ActiveTuningTargetResponseError(
            `SetActiveTuningTarget acknowledged ${parsed.centerHz} Hz instead of ${normalizedCenterHz} Hz.`
          );
        }
        this.activeTuningTargetSupport = 'supported';
        return {
          command: 'SetActiveTuningTarget',
          mode: 'target-aware',
          targetId: parsed.targetId,
          centerHz: parsed.centerHz,
          fallbackReason: null,
          response,
        };
      }

      if (parsed.kind === 'error') {
        this.activeTuningTargetSupport = 'supported';
        throw new ActiveTuningTargetResponseError(formatActiveTuningTargetError(parsed));
      }

      if (isUnsupportedCommandMessage(response)) {
        this.activeTuningTargetSupport = 'unsupported';
      }
      return this.setCenterFrequencyFallback(
        normalizedCenterHz,
        `SetActiveTuningTarget returned no target-aware acknowledgement: ${formatCommandResponse(response)}`
      );
    } catch (error) {
      const fallbackReason = describeError(error);
      if (error instanceof ActiveTuningTargetResponseError || !shouldFallbackToCenterFrequency(error)) {
        throw error instanceof Error ? error : new Error(fallbackReason);
      }
      if (isUnsupportedCommandMessage(fallbackReason)) {
        this.activeTuningTargetSupport = 'unsupported';
      }
      return this.setCenterFrequencyFallback(normalizedCenterHz, fallbackReason);
    }
  }

  private async setCenterFrequencyFallback(
    centerHz: number,
    fallbackReason: string
  ): Promise<SetActiveTuningTargetResult> {
    try {
      await this.sendTextCommand(`SetCenterFrequency ${centerHz}`);
      return {
        command: 'SetCenterFrequency',
        mode: 'legacy-center-frequency',
        targetId: null,
        centerHz,
        fallbackReason,
        response: null,
      };
    } catch (error) {
      throw new Error(
        `SetActiveTuningTarget failed (${fallbackReason}); ` +
          `SetCenterFrequency fallback failed (${describeError(error)}).`
      );
    }
  }

  async waitForCenterFrequency(targetHz: number, timeoutMs = 3000): Promise<boolean> {
    const current = this.latestSettingsStatus?.centerFrequency;
    if (current === targetHz) {
      return true;
    }

    return new Promise((resolve) => {
      let settled = false;
      const unsubscribe = this.onSettingsStatus((settings) => {
        if (settings.centerFrequency === targetHz) {
          if (!settled) {
            settled = true;
            unsubscribe();
            window.clearTimeout(timeoutId);
            resolve(true);
          }
        }
      });

      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        unsubscribe();
        resolve(false);
      }, timeoutMs);
    });
  }

  applyFactoryInfo(factory: Record<string, string>): void {
    if (!this.deviceInfo) {
      return;
    }

    const serial = factory['Serial Number'];
    const macAddress = factory['MAC Address'];
    const model = factory.Model;
    const firmware = factory.Firmware;
    const hardware = factory.Hardware;
    const manufacturer = factory.Manufacturer;

    if (serial) {
      this.deviceInfo.serialNumber = serial;
    }
    if (macAddress) {
      this.deviceInfo.macAddress = macAddress;
    }
    if (model) {
      this.deviceInfo.model = model;
    }
    if (firmware) {
      this.deviceInfo.firmwareRevision = firmware;
    }
    if (hardware) {
      this.deviceInfo.hardwareRevision = hardware;
    }
    if (manufacturer) {
      this.deviceInfo.manufacturer = manufacturer;
    }

    this.emitDiagnostic('info', 'factory_info_applied', {
      serialNumber: this.deviceInfo.serialNumber ?? null,
      model: this.deviceInfo.model ?? null,
      firmwareRevision: this.deviceInfo.firmwareRevision ?? null,
      hardwareRevision: this.deviceInfo.hardwareRevision ?? null,
      manufacturer: this.deviceInfo.manufacturer ?? null,
      parsedFields: Object.keys(factory),
    });
    this.emitDeviceInfoChanged();
  }

  async refreshStatusFromCommand(timeoutMs = 2500): Promise<boolean> {
    if (!this.toolboxCommandCharacteristic || !this.deviceInfo) {
      return false;
    }

    let response = '';
    try {
      response = await this.sendTextCommandWithResponse('getStatus', timeoutMs);
    } catch {
      return false;
    }

    const status = parseGetStatusResponse(response);
    if (!status) {
      return false;
    }

    let updated = false;
    if (status.currentFrequency != null) {
      this.deviceInfo.currentFrequency = status.currentFrequency;
      updated = true;
    }
    if (status.current != null) {
      this.deviceInfo.current = status.current;
      updated = true;
    }
    if (status.powerWatts != null) {
      this.deviceInfo.powerWatts = status.powerWatts;
      updated = true;
    }
    if (status.voltageBattery != null) {
      this.deviceInfo.voltageBattery = status.voltageBattery;
      updated = true;
    }
    if (status.temperaturePCB != null) {
      this.deviceInfo.temperaturePCB = status.temperaturePCB;
      updated = true;
    }
    if (status.temperatureBattery != null) {
      this.deviceInfo.temperatureBattery = status.temperatureBattery;
      updated = true;
    }
    if (status.temperatureESP32 != null) {
      this.deviceInfo.temperatureESP32 = status.temperatureESP32;
      updated = true;
    }

    if (updated) {
      this.emitDeviceInfoChanged();
    }

    return updated;
  }

  private async setupOtaCharacteristics(): Promise<void> {
    if (!this.server) {
      return;
    }

    try {
      const otaService = await this.server.getPrimaryService(OTA_SERVICE_UUID);
      this.otaFirmwareCharacteristic = await otaService.getCharacteristic(OTA_FIRMWARE_CHAR_UUID);
      this.otaCommandCharacteristic = await otaService.getCharacteristic(OTA_COMMAND_CHAR_UUID);
      this.capabilities.ota = true;
    } catch {
      this.capabilities.ota = false;
      this.otaFirmwareCharacteristic = null;
      this.otaCommandCharacteristic = null;
    }
  }

  private async setupToolboxCharacteristics(): Promise<void> {
    if (!this.server) {
      return;
    }

    const services = (await Promise.all([
      this.getOptionalPrimaryService(TOOLBOX_SERVICE_UUID),
      this.getOptionalPrimaryService(TOOLBOX_STATUS_SERVICE_UUID),
    ])).filter((service): service is BluetoothRemoteGATTService => Boolean(service));

    if (services.length === 0) {
      this.capabilities.command = false;
      this.capabilities.powerStatus = false;
      this.capabilities.temperatureStatus = false;
      this.capabilities.settingsStatus = false;
      return;
    }

    this.toolboxCommandCharacteristic = await this.findOptionalCharacteristic(
      services,
      TOOLBOX_COMMAND_CHAR_UUID
    );
    this.powerStatusCharacteristic = await this.findOptionalCharacteristic(
      services,
      TOOLBOX_POWER_STATUS_CHAR_UUID
    );
    this.temperatureStatusCharacteristic = await this.findOptionalCharacteristic(
      services,
      TOOLBOX_TEMPERATURE_STATUS_CHAR_UUID
    );
    this.settingsStatusCharacteristic = await this.findOptionalCharacteristic(
      services,
      TOOLBOX_SETTINGS_STATUS_CHAR_UUID
    );

    this.capabilities.command = Boolean(this.toolboxCommandCharacteristic);
    this.capabilities.powerStatus = Boolean(this.powerStatusCharacteristic);
    this.capabilities.temperatureStatus = Boolean(this.temperatureStatusCharacteristic);
    this.capabilities.settingsStatus = Boolean(this.settingsStatusCharacteristic);

    await this.startToolboxNotifications();
  }

  private async getOptionalPrimaryService(
    uuid: BluetoothServiceUUID
  ): Promise<BluetoothRemoteGATTService | null> {
    if (!this.server) {
      return null;
    }
    try {
      return await this.server.getPrimaryService(uuid);
    } catch {
      return null;
    }
  }

  private async findOptionalCharacteristic(
    services: BluetoothRemoteGATTService[],
    uuid: BluetoothCharacteristicUUID
  ): Promise<BluetoothRemoteGATTCharacteristic | null> {
    for (let index = 0; index < services.length; index += 1) {
      const service = services[index];
      const characteristic = await this.getOptionalCharacteristic(service, uuid);
      if (characteristic) {
        return characteristic;
      }
    }
    return null;
  }

  private async startToolboxNotifications(): Promise<void> {
    if (this.toolboxCommandCharacteristic?.properties.notify) {
      this.toolboxCommandListener = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        if (target?.value) {
          this.handleToolboxCommandNotification(target.value);
        }
      };
      await this.toolboxCommandCharacteristic.startNotifications();
      this.toolboxCommandCharacteristic.addEventListener(
        'characteristicvaluechanged',
        this.toolboxCommandListener
      );
    }

    if (this.powerStatusCharacteristic?.properties.notify) {
      this.powerStatusListener = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        if (target?.value) {
          const parsed = parsePowerStatus(this.decodeUtf8(target.value).trim());
          this.recordNotification('power', target.value.byteLength, Boolean(parsed), parsed ?? undefined);
          if (parsed) {
            this.latestPowerStatus = parsed;
            if (this.deviceInfo) {
              this.deviceInfo.voltageBattery = parsed.voltageBattery;
              this.deviceInfo.current = parsed.current;
              this.deviceInfo.powerWatts = parsed.powerWatts;
            }
            this.emitDeviceInfoChanged();
          }
        }
      };
      await this.powerStatusCharacteristic.startNotifications();
      this.powerStatusCharacteristic.addEventListener(
        'characteristicvaluechanged',
        this.powerStatusListener
      );
    }

    if (this.temperatureStatusCharacteristic?.properties.notify) {
      this.temperatureStatusListener = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        if (target?.value) {
          const parsed = parseTemperatureStatus(this.decodeUtf8(target.value).trim());
          this.recordNotification(
            'temperature',
            target.value.byteLength,
            Boolean(parsed),
            parsed ?? undefined
          );
          if (parsed) {
            this.latestTemperatureStatus = parsed;
            if (this.deviceInfo) {
              this.deviceInfo.temperaturePCB = parsed.temperaturePCB;
              this.deviceInfo.temperatureESP32 = parsed.temperatureESP32;
              this.deviceInfo.temperatureBattery = parsed.temperatureBattery;
            }
            this.emitDeviceInfoChanged();
          }
        }
      };
      await this.temperatureStatusCharacteristic.startNotifications();
      this.temperatureStatusCharacteristic.addEventListener(
        'characteristicvaluechanged',
        this.temperatureStatusListener
      );
    }

    if (this.settingsStatusCharacteristic?.properties.notify) {
      this.settingsStatusListener = (event: Event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic;
        if (target?.value) {
          const parsed = parseSettingsStatus(this.decodeUtf8(target.value).trim());
          this.recordNotification(
            'settings',
            target.value.byteLength,
            Boolean(parsed),
            parsed ? buildSettingsDiagnosticSnapshot(parsed) : undefined
          );
          if (parsed) {
            const previous = this.latestSettingsStatus;
            this.latestSettingsStatus = parsed;
            if (hasSettingsDiagnosticStateChanged(previous, parsed)) {
              this.emitDiagnostic('info', 'settings_state_changed', {
                ...buildSettingsDiagnosticSnapshot(parsed),
                notificationByteLength: target.value.byteLength,
              });
            }
            if (this.deviceInfo) {
              this.deviceInfo.centerFrequency = parsed.centerFrequency;
              this.deviceInfo.currentFrequency = parsed.currentFrequency;
              this.deviceInfo.autoTuneMode = parsed.autoTuneMode;
              this.deviceInfo.sweepActive = parsed.sweepActive;
              this.deviceInfo.odometerTotalMinutes = parsed.odometerTotalMinutes;
            }
            this.settingsHandlers.forEach((handler) => handler(parsed));
            this.emitDeviceInfoChanged();
          }
        }
      };
      await this.settingsStatusCharacteristic.startNotifications();
      this.settingsStatusCharacteristic.addEventListener(
        'characteristicvaluechanged',
        this.settingsStatusListener
      );
    }
  }

  private handleToolboxCommandNotification(view: DataView): void {
    this.recordNotification('command', view.byteLength, view.byteLength > 0);
    const chunk = this.decodeUtf8(view).replace(/\r/g, '');
    if (!chunk) {
      return;
    }

    this.commandLineBuffer += chunk;
    this.emitDelimitedCommandLines();
    this.emitLeadingSweepTokens();
    this.scheduleCommandBufferFlush();
  }

  private emitDelimitedCommandLines(): void {
    if (!this.commandLineBuffer.includes('\n')) {
      return;
    }
    const lines = this.commandLineBuffer.split('\n');
    this.commandLineBuffer = lines.pop() ?? '';
    lines.forEach((line) => this.emitTextLine(line));
  }

  private emitLeadingSweepTokens(): void {
    while (true) {
      const trimmed = this.commandLineBuffer.trimStart();
      if (!trimmed) {
        this.commandLineBuffer = '';
        return;
      }

      if (trimmed.startsWith('sweepEnd')) {
        this.commandLineBuffer = trimmed.slice('sweepEnd'.length);
        this.emitTextLine('sweepEnd');
        continue;
      }

      const match = trimmed.match(SWEEP_TEXT_TOKEN_REGEX);
      if (!match) {
        break;
      }

      const token = match[0];
      this.commandLineBuffer = trimmed.slice(token.length);
      this.emitTextLine(token);
    }
  }

  private scheduleCommandBufferFlush(): void {
    if (this.commandBufferFlushTimer) {
      window.clearTimeout(this.commandBufferFlushTimer);
      this.commandBufferFlushTimer = null;
    }
    if (!this.commandLineBuffer.trim()) {
      this.commandLineBuffer = '';
      return;
    }
    this.commandBufferFlushTimer = window.setTimeout(() => {
      this.commandBufferFlushTimer = null;
      const pending = this.commandLineBuffer.trim();
      this.commandLineBuffer = '';
      if (pending) {
        this.emitTextLine(pending);
      }
    }, COMMAND_BUFFER_IDLE_FLUSH_MS);
  }

  private emitTextLine(line: string): void {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    this.textLineHandlers.forEach((handler) => handler(trimmed));
  }

  private async loadDeviceInfo(): Promise<void> {
    if (!this.server || !this.deviceInfo) {
      return;
    }

    let infoService: BluetoothRemoteGATTService | null = null;
    try {
      infoService = await this.server.getPrimaryService(DEVICE_INFO_SERVICE_UUID);
    } catch {
      infoService = null;
    }

    if (!infoService) {
      return;
    }

    const manufacturer = await this.readOptionalString(infoService, MANUFACTURER_NAME_CHAR_UUID);
    const firmwareRevision = await this.readOptionalString(infoService, FIRMWARE_REVISION_CHAR_UUID);
    const hardwareRevision = await this.readOptionalString(infoService, HARDWARE_REVISION_CHAR_UUID);

    if (manufacturer) {
      this.deviceInfo.manufacturer = manufacturer;
    }
    if (firmwareRevision) {
      this.deviceInfo.firmwareRevision = firmwareRevision;
    }
    if (hardwareRevision) {
      this.deviceInfo.hardwareRevision = hardwareRevision;
    }
  }

  private async getOptionalCharacteristic(
    service: BluetoothRemoteGATTService,
    uuid: BluetoothCharacteristicUUID
  ): Promise<BluetoothRemoteGATTCharacteristic | null> {
    try {
      return await service.getCharacteristic(uuid);
    } catch {
      return null;
    }
  }

  private async readOptionalString(
    service: BluetoothRemoteGATTService,
    uuid: BluetoothCharacteristicUUID
  ): Promise<string | undefined> {
    try {
      const characteristic = await service.getCharacteristic(uuid);
      const value = await characteristic.readValue();
      const text = this.decodeUtf8(value).trim();
      return text || undefined;
    } catch {
      return undefined;
    }
  }

  private decodeUtf8(value: DataView): string {
    const bytes = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    return new TextDecoder('utf-8').decode(bytes);
  }

  private emitDeviceInfoChanged(): void {
    this.deviceInfoHandlers.forEach((handler) => handler(this.deviceInfo));
  }

  private handleDisconnect = (): void => {
    const keepDevice = this.keepDeviceOnDisconnect;
    this.emitDiagnostic('warning', 'gatt_disconnected', {
      keepDevice,
      notificationMetrics: this.notificationMetrics,
    });
    this.reset(keepDevice);
    this.disconnectHandlers.forEach((handler) => handler());
  };

  private resetSessionState(): void {
    this.commandUnlocked = false;
    this.commandLineBuffer = '';
    if (this.commandBufferFlushTimer) {
      window.clearTimeout(this.commandBufferFlushTimer);
      this.commandBufferFlushTimer = null;
    }
    this.latestPowerStatus = null;
    this.latestTemperatureStatus = null;
    this.latestSettingsStatus = null;
    this.deviceInfo = null;
    this.activeTuningTargetSupport = 'unknown';
    this.notificationMetrics = createNotificationMetrics();
    this.capabilities = {
      ota: false,
      command: false,
      powerStatus: false,
      temperatureStatus: false,
      settingsStatus: false,
    };

    if (this.otaCommandCharacteristic && this.otaCommandListener) {
      this.otaCommandCharacteristic.removeEventListener('characteristicvaluechanged', this.otaCommandListener);
    }
    if (this.otaFirmwareCharacteristic && this.otaFirmwareListener) {
      this.otaFirmwareCharacteristic.removeEventListener('characteristicvaluechanged', this.otaFirmwareListener);
    }
    if (this.toolboxCommandCharacteristic && this.toolboxCommandListener) {
      this.toolboxCommandCharacteristic.removeEventListener('characteristicvaluechanged', this.toolboxCommandListener);
    }
    if (this.powerStatusCharacteristic && this.powerStatusListener) {
      this.powerStatusCharacteristic.removeEventListener('characteristicvaluechanged', this.powerStatusListener);
    }
    if (this.temperatureStatusCharacteristic && this.temperatureStatusListener) {
      this.temperatureStatusCharacteristic.removeEventListener('characteristicvaluechanged', this.temperatureStatusListener);
    }
    if (this.settingsStatusCharacteristic && this.settingsStatusListener) {
      this.settingsStatusCharacteristic.removeEventListener('characteristicvaluechanged', this.settingsStatusListener);
    }

    this.otaCommandCharacteristic = null;
    this.otaFirmwareCharacteristic = null;
    this.toolboxCommandCharacteristic = null;
    this.powerStatusCharacteristic = null;
    this.temperatureStatusCharacteristic = null;
    this.settingsStatusCharacteristic = null;

    this.otaCommandListener = null;
    this.otaFirmwareListener = null;
    this.toolboxCommandListener = null;
    this.powerStatusListener = null;
    this.temperatureStatusListener = null;
    this.settingsStatusListener = null;
  }

  private reset(keepDevice: boolean): void {
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', this.handleDisconnect);
    }
    if (!keepDevice) {
      this.device = null;
    }
    this.server = null;
    this.resetSessionState();
    this.emitDeviceInfoChanged();
  }

  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    timeoutMessage: string
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);

      promise
        .then((value) => {
          window.clearTimeout(timeoutId);
          resolve(value);
        })
        .catch((error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        });
    });
  }

  private async delay(timeoutMs: number): Promise<void> {
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), timeoutMs);
    });
  }

  private recordNotification(
    kind: BleNotificationKind,
    byteLength: number,
    parsed: boolean,
    snapshot?: Record<string, unknown>
  ): void {
    const metric = this.notificationMetrics[kind];
    metric.received += 1;
    metric.lastByteLength = byteLength;
    metric.lastReceivedAtIso = new Date().toISOString();
    if (parsed) {
      metric.parsed += 1;
    } else {
      metric.parseFailures += 1;
    }

    const shouldReportParseFailure =
      !parsed && (metric.parseFailures === 1 || metric.parseFailures % 25 === 0);
    if (shouldReportParseFailure || (parsed && (metric.received === 1 || metric.received % 25 === 0))) {
      this.emitDiagnostic(parsed ? 'debug' : 'warning', 'notification_health', {
        characteristic: kind,
        byteLength,
        parsed,
        received: metric.received,
        parsedCount: metric.parsed,
        parseFailures: metric.parseFailures,
        ...(snapshot ?? {}),
      });
    }
  }

  private emitDiagnostic(
    level: BleDiagnosticEvent['level'],
    event: string,
    details?: Record<string, unknown>
  ): void {
    this.diagnosticHandlers.forEach((handler) => {
      try {
        handler({ level, event, details });
      } catch {
        // Diagnostic logging must never disrupt Bluetooth behavior.
      }
    });
  }
}

function createNotificationMetrics(): Record<BleNotificationKind, NotificationMetric> {
  const empty = (): NotificationMetric => ({
    received: 0,
    parsed: 0,
    parseFailures: 0,
    lastByteLength: 0,
    lastReceivedAtIso: null,
  });
  return {
    command: empty(),
    power: empty(),
    temperature: empty(),
    settings: empty(),
  };
}

function describeTextCommand(command: string): string {
  if (command === COMMAND_UNLOCK_PASSPHRASE) {
    return '[command-channel-unlock]';
  }
  return command.slice(0, 160);
}

function describeDiagnosticError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }
  return {
    name: 'UnknownError',
    message: String(error ?? 'Unknown error'),
  };
}

function buildSettingsDiagnosticSnapshot(settings: SettingsStatus): Record<string, unknown> {
  return {
    buttonPhysicallyPressed: settings.buttonPhysicallyPressed,
    buttonEnabled: settings.buttonEnabled,
    autotuneActive: settings.autotuneActive,
    sweepActive: settings.sweepActive,
    qiCharging: settings.qiCharging,
    healthCheckCriticalStop: settings.healthCheckCriticalStop,
    warningState: settings.warningState,
    lowBatteryState: settings.lowBatteryState,
    overheatState: settings.overheatState,
    centerFrequency: settings.centerFrequency,
    currentFrequency: settings.currentFrequency,
    autoTuneMode: settings.autoTuneMode,
  };
}

function hasSettingsDiagnosticStateChanged(
  previous: SettingsStatus | null,
  current: SettingsStatus
): boolean {
  if (!previous) {
    return true;
  }
  return (
    previous.buttonPhysicallyPressed !== current.buttonPhysicallyPressed ||
    previous.buttonEnabled !== current.buttonEnabled ||
    previous.autotuneActive !== current.autotuneActive ||
    previous.sweepActive !== current.sweepActive ||
    previous.qiCharging !== current.qiCharging ||
    previous.healthCheckCriticalStop !== current.healthCheckCriticalStop ||
    previous.warningState !== current.warningState ||
    previous.lowBatteryState !== current.lowBatteryState ||
    previous.overheatState !== current.overheatState ||
    previous.autoTuneMode !== current.autoTuneMode
  );
}

type ParsedActiveTuningTargetResponse =
  | {
      kind: 'ok';
      targetId: string;
      centerHz: number;
    }
  | {
      kind: 'error';
      code: string;
      detail: string;
    }
  | {
      kind: 'unrecognized';
    };

class ActiveTuningTargetResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActiveTuningTargetResponseError';
  }
}

function parseActiveTuningTargetResponse(response: string): ParsedActiveTuningTargetResponse {
  const lines = response
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    if (line.startsWith('ACTIVE_TARGET_OK|')) {
      const [, targetId = '', centerText = ''] = line.split('|');
      const centerHz = Number(centerText);
      if (!targetId.trim() || !Number.isFinite(centerHz)) {
        return {
          kind: 'error',
          code: 'malformed_ok',
          detail: line,
        };
      }
      return {
        kind: 'ok',
        targetId: targetId.trim(),
        centerHz: Math.round(centerHz),
      };
    }

    if (line.startsWith('ACTIVE_TARGET_ERROR|')) {
      const parts = line.split('|');
      return {
        kind: 'error',
        code: parts[1]?.trim() || 'unknown',
        detail: parts.slice(2).join('|').trim(),
      };
    }
  }

  return { kind: 'unrecognized' };
}

function formatActiveTuningTargetError(
  parsed: Extract<ParsedActiveTuningTargetResponse, { kind: 'error' }>
): string {
  return parsed.detail
    ? `SetActiveTuningTarget failed (${parsed.code}: ${parsed.detail}).`
    : `SetActiveTuningTarget failed (${parsed.code}).`;
}

function shouldFallbackToCenterFrequency(error: unknown): boolean {
  const message = describeError(error).toLowerCase();
  return (
    isUnsupportedCommandMessage(message) ||
    message.includes('command timed out') ||
    message.includes('no response received')
  );
}

function isUnsupportedCommandMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('unsupported') ||
    normalized.includes('unknown command') ||
    normalized.includes('unrecognized command') ||
    normalized.includes('invalid command')
  );
}

function formatCommandResponse(response: string): string {
  const compact = response.replace(/\s+/g, ' ').trim();
  return compact || 'empty response';
}

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Unknown error';
}
